import type { PartyRef, RuleBody } from '@kept/schedule';
import { ruleBody } from '@kept/shared';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { api, bodyOf, type Plan, type RuleVersion } from '../lib/api.ts';
import { addDays, today } from '../lib/plantime.ts';
import { breakName, describeRule, holidayName, type Names } from '../lib/describe.ts';
import { ErrorNote, Field, useAction } from '../ui.tsx';

type Type = RuleBody['type'];

const TYPES: { type: Type; title: string; hint: string }[] = [
  { type: 'rotation', title: 'Regular schedule', hint: 'Every other weekend, alternating weeks' },
  { type: 'midweek', title: 'Midweek visit', hint: 'An evening every week or two' },
  { type: 'schoolBreak', title: 'School break', hint: 'Spring, Thanksgiving, winter' },
  { type: 'holiday', title: 'Holiday', hint: 'A named day, alternating years' },
  { type: 'specialDay', title: 'Special day', hint: 'Mother’s or Father’s Day, a birthday' },
  { type: 'block', title: 'Dated block', hint: 'Summer weeks, a one-off trip' },
];

const HOLIDAY_IDS = [
  'newYearsDay', 'mlkDay', 'presidentsDay', 'easter', 'memorialDay', 'juneteenth', 'independenceDay',
  'laborDay', 'columbusDay', 'veteransDay', 'thanksgiving', 'christmasEve', 'christmasDay', 'newYearsEve',
] as const;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Everything any rule type needs, as form strings. */
interface Draft {
  type: Type;
  party: PartyRef;
  anchor: string;
  cycle: string;
  startTime: string;
  endTime: string;
  spanDays: string;
  everyWeeks: string;
  label: string;
  wholeDays: boolean;
  firstDay: string;
  lastDay: string;
  startAt: string;
  endAt: string;
  breakKind: string;
  fromFixed: boolean;
  fromMonth: string;
  fromDay: string;
  fromTime: string;
  toFixed: boolean;
  toMonth: string;
  toDay: string;
  toTime: string;
  releaseTime: string;
  returnTime: string;
  parityYear: 'start' | 'end';
  specialDay: 'mothersDay' | 'fathersDay' | 'date';
  month: string;
  day: string;
  holiday: (typeof HOLIDAY_IDS)[number];
  startOffset: string;
  endOffset: string;
  adjacent: boolean;
}

function blankDraft(plan: Plan): Draft {
  const measured = plan.measuredPartyId ?? plan.parties[0]!.id;
  const d = today(plan.timeZone);
  return {
    type: 'rotation', party: measured, anchor: d, cycle: '14', startTime: '18:00', endTime: '17:00', spanDays: '2',
    everyWeeks: '2', label: '', wholeDays: true, firstDay: d, lastDay: d, startAt: `${d}T00:00`, endAt: `${d}T00:00`,
    breakKind: 'spring', fromFixed: false, fromMonth: '12', fromDay: '28', fromTime: '17:00', toFixed: false, toMonth: '12',
    toDay: '28', toTime: '17:00', releaseTime: '15:00', returnTime: '08:00', parityYear: 'start', specialDay: 'mothersDay',
    month: '1', day: '1', holiday: 'thanksgiving', startOffset: '-1', endOffset: '1', adjacent: false,
  };
}

/** A stored rule back into form fields, for a new version of it. */
function draftFrom(plan: Plan, body: RuleBody): Draft {
  const d = { ...blankDraft(plan), type: body.type };
  switch (body.type) {
    case 'rotation': {
      const s = body.segments[0]!;
      return { ...d, party: s.party, anchor: addDays(body.anchor, s.startDay), cycle: String(body.cycleDays), startTime: s.startTime, endTime: s.endTime, spanDays: String(s.endDay - s.startDay) };
    }
    case 'midweek':
      return { ...d, party: body.party, anchor: body.anchor, everyWeeks: String(body.everyWeeks), startTime: body.startTime, endTime: body.endTime };
    case 'block': {
      const whole = body.start.endsWith('T00:00') && body.end.endsWith('T00:00');
      return { ...d, party: body.party, label: body.label, wholeDays: whole, firstDay: body.start.slice(0, 10), lastDay: addDays(body.end.slice(0, 10), -1), startAt: body.start, endAt: body.end };
    }
    case 'schoolBreak':
      return {
        ...d, party: body.party, breakKind: body.breakKind, releaseTime: body.releaseTime, returnTime: body.returnTime, parityYear: body.parityYear ?? 'start',
        fromFixed: body.from !== 'release', ...(body.from !== 'release' ? { fromMonth: String(body.from.month), fromDay: String(body.from.day), fromTime: body.from.time } : {}),
        toFixed: body.to !== 'return', ...(body.to !== 'return' ? { toMonth: String(body.to.month), toDay: String(body.to.day), toTime: body.to.time } : {}),
      };
    case 'specialDay':
      return typeof body.day === 'string'
        ? { ...d, party: body.party, specialDay: body.day, startTime: body.startTime, endTime: body.endTime }
        : { ...d, party: body.party, specialDay: 'date', month: String(body.day.month), day: String(body.day.day), startTime: body.startTime, endTime: body.endTime };
    case 'holiday':
      return { ...d, party: body.party, holiday: body.holiday, startOffset: String(body.startOffsetDays), startTime: body.startTime, endOffset: String(body.endOffsetDays), endTime: body.endTime, adjacent: body.includeAdjacentWeekend ?? false };
  }
}

function toBody(d: Draft): unknown {
  const int = (s: string) => (s.trim() === '' ? NaN : Number(s));
  switch (d.type) {
    case 'rotation':
      return { type: 'rotation', anchor: d.anchor, cycleDays: int(d.cycle), segments: [{ startDay: 0, startTime: d.startTime, endDay: int(d.spanDays), endTime: d.endTime, party: d.party }] };
    case 'midweek':
      return { type: 'midweek', anchor: d.anchor, everyWeeks: int(d.everyWeeks), startTime: d.startTime, endTime: d.endTime, party: d.party };
    case 'block':
      return d.wholeDays
        ? { type: 'block', label: d.label.trim(), start: `${d.firstDay}T00:00`, end: `${addDays(d.lastDay, 1)}T00:00`, party: d.party }
        : { type: 'block', label: d.label.trim(), start: d.startAt.slice(0, 16), end: d.endAt.slice(0, 16), party: d.party };
    case 'schoolBreak':
      return {
        type: 'schoolBreak', breakKind: d.breakKind.trim(), releaseTime: d.releaseTime, returnTime: d.returnTime, party: d.party, parityYear: d.parityYear,
        from: d.fromFixed ? { month: int(d.fromMonth), day: int(d.fromDay), time: d.fromTime } : 'release',
        to: d.toFixed ? { month: int(d.toMonth), day: int(d.toDay), time: d.toTime } : 'return',
      };
    case 'specialDay':
      return { type: 'specialDay', day: d.specialDay === 'date' ? { month: int(d.month), day: int(d.day) } : d.specialDay, startTime: d.startTime, endTime: d.endTime, party: d.party };
    case 'holiday':
      return { type: 'holiday', holiday: d.holiday, startOffsetDays: int(d.startOffset), startTime: d.startTime, endOffsetDays: int(d.endOffset), endTime: d.endTime, party: d.party, includeAdjacentWeekend: d.adjacent };
  }
}

interface Props {
  plan: Plan;
  names: Names;
  /** The rule to change; absent for a new rule. */
  current?: RuleVersion;
  onSaved: () => void;
}

export function RuleForm({ plan, names, current, onSaved }: Props) {
  const [d, setD] = useState<Draft>(() => (current ? draftFrom(plan, bodyOf(current)) : blankDraft(plan)));
  const [clause, setClause] = useState(current?.clause ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(current ? today(plan.timeZone) : '');
  const [reason, setReason] = useState('');
  const [tier, setTier] = useState(current?.tier != null ? String(current.tier) : '');
  const { busy, error, setError, run } = useAction();
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));

  const parsed = useMemo(() => ruleBody.safeParse(toBody(d)), [d]);
  const sentence = parsed.success ? describeRule(parsed.data, names) : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!parsed.success) return setError(parsed.error.issues.map((i) => i.message).join(' '));
    const input = {
      effectiveFrom,
      clause: clause.trim(),
      rule: parsed.data,
      ...(tier.trim() ? { tier: Number(tier) } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    };
    void run(async () => {
      if (current) await api.addRuleVersion(plan.id, current.ruleId, input);
      else await api.addRule(plan.id, input);
      onSaved();
    });
  };

  const time = (k: 'startTime' | 'endTime' | 'releaseTime' | 'returnTime' | 'fromTime' | 'toTime', label: string, hint?: string) => (
    <Field label={label} hint={hint}>
      <input className="input" type="time" required value={d[k]} onChange={(e) => set(k, e.target.value)} />
    </Field>
  );
  const party = (alternate: boolean) => (
    <PartyPicker value={d.party} onChange={(p) => set('party', p)} plan={plan} alternate={alternate} />
  );

  return (
    <form className="form" onSubmit={submit}>
      {!current && (
        <fieldset className="field">
          <legend>What kind of rule?</legend>
          <div className="kinds">
            {TYPES.map((t) => (
              <button type="button" className="kind" key={t.type} aria-pressed={d.type === t.type} onClick={() => set('type', t.type)}>
                <b>{t.title}</b>
                <small>{t.hint}</small>
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {d.type === 'rotation' && (
        <>
          <div className="two">
            <Field label="First day of one visit" hint="e.g. the Friday one of their weekends starts">
              <input className="input" type="date" required value={d.anchor} onChange={(e) => set('anchor', e.target.value)} />
            </Field>
            <Field label="Repeats">
              <select className="input" value={d.cycle} onChange={(e) => set('cycle', e.target.value)}>
                <option value="7">Every week</option>
                <option value="14">Every other week</option>
                <option value="21">Every 3 weeks</option>
                <option value="28">Every 4 weeks</option>
              </select>
            </Field>
          </div>
          <div className="three">
            {time('startTime', 'Starts at')}
            <Field label="Ends">
              <select className="input" value={d.spanDays} onChange={(e) => set('spanDays', e.target.value)}>
                {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n === 0 ? 'Same day' : n === 1 ? 'Next day' : `${n} days later`}
                  </option>
                ))}
              </select>
            </Field>
            {time('endTime', 'Ends at')}
          </div>
          {party(false)}
        </>
      )}

      {d.type === 'midweek' && (
        <>
          <div className="two">
            <Field label="Date of one visit" hint="Sets the weekday and which weeks">
              <input className="input" type="date" required value={d.anchor} onChange={(e) => set('anchor', e.target.value)} />
            </Field>
            <Field label="Repeats">
              <select className="input" value={d.everyWeeks} onChange={(e) => set('everyWeeks', e.target.value)}>
                <option value="1">Every week</option>
                <option value="2">Every other week</option>
              </select>
            </Field>
          </div>
          <div className="two">
            {time('startTime', 'From')}
            {time('endTime', 'To')}
          </div>
          {party(false)}
        </>
      )}

      {d.type === 'block' && (
        <>
          <Field label="Name" hint="e.g. Summer 2026">
            <input className="input" required value={d.label} onChange={(e) => set('label', e.target.value)} />
          </Field>
          <label className="check">
            <input type="checkbox" checked={d.wholeDays} onChange={(e) => set('wholeDays', e.target.checked)} />
            Whole days, midnight to midnight
          </label>
          {d.wholeDays ? (
            <div className="two">
              <Field label="First day">
                <input className="input" type="date" required value={d.firstDay} onChange={(e) => set('firstDay', e.target.value)} />
              </Field>
              <Field label="Last day">
                <input className="input" type="date" required value={d.lastDay} onChange={(e) => set('lastDay', e.target.value)} />
              </Field>
            </div>
          ) : (
            <div className="two">
              <Field label="Starts">
                <input className="input" type="datetime-local" required value={d.startAt} onChange={(e) => set('startAt', e.target.value)} />
              </Field>
              <Field label="Ends">
                <input className="input" type="datetime-local" required value={d.endAt} onChange={(e) => set('endAt', e.target.value)} />
              </Field>
            </div>
          )}
          {party(false)}
        </>
      )}

      {d.type === 'schoolBreak' && (
        <>
          <Field label="Which break" hint="Matches the breaks you enter under School breaks">
            <select className="input" value={d.breakKind} onChange={(e) => set('breakKind', e.target.value)}>
              {['spring', 'thanksgiving', 'winter', 'fall'].map((k) => (
                <option key={k} value={k}>
                  {breakName(k)}
                </option>
              ))}
            </select>
          </Field>
          <Edge
            label="Starts"
            fixed={d.fromFixed}
            onFixed={(v) => set('fromFixed', v)}
            plain="When school lets out for the break"
            month={d.fromMonth}
            day={d.fromDay}
            time={d.fromTime}
            onChange={(m, dd, t) => setD((x) => ({ ...x, fromMonth: m, fromDay: dd, fromTime: t }))}
          />
          <Edge
            label="Ends"
            fixed={d.toFixed}
            onFixed={(v) => set('toFixed', v)}
            plain="When school starts again"
            month={d.toMonth}
            day={d.toDay}
            time={d.toTime}
            onChange={(m, dd, t) => setD((x) => ({ ...x, toMonth: m, toDay: dd, toTime: t }))}
          />
          <div className="two">
            {time('releaseTime', 'School lets out at', 'On the last day before the break')}
            {time('returnTime', 'School starts at', 'On the first day back')}
          </div>
          {party(true)}
          {typeof d.party !== 'string' && (
            <Field label="For a break that spans New Year, even or odd follows">
              <select className="input" value={d.parityYear} onChange={(e) => set('parityYear', e.target.value as 'start' | 'end')}>
                <option value="start">The year the break starts</option>
                <option value="end">The year the break ends</option>
              </select>
            </Field>
          )}
        </>
      )}

      {d.type === 'specialDay' && (
        <>
          <Field label="Which day">
            <select className="input" value={d.specialDay} onChange={(e) => set('specialDay', e.target.value as Draft['specialDay'])}>
              <option value="mothersDay">Mother’s Day</option>
              <option value="fathersDay">Father’s Day</option>
              <option value="date">A date every year (a birthday)</option>
            </select>
          </Field>
          {d.specialDay === 'date' && <MonthDay month={d.month} day={d.day} onChange={(m, dd) => setD((x) => ({ ...x, month: m, day: dd }))} />}
          <div className="two">
            {time('startTime', 'From')}
            {time('endTime', 'To')}
          </div>
          {party(true)}
        </>
      )}

      {d.type === 'holiday' && (
        <>
          <Field label="Holiday">
            <select className="input" value={d.holiday} onChange={(e) => set('holiday', e.target.value as Draft['holiday'])}>
              {HOLIDAY_IDS.map((h) => (
                <option key={h} value={h}>
                  {holidayName(h)}
                </option>
              ))}
            </select>
          </Field>
          <div className="two">
            <Offset label="Starts" value={d.startOffset} onChange={(v) => set('startOffset', v)} />
            {time('startTime', 'At')}
          </div>
          <div className="two">
            <Offset label="Ends" value={d.endOffset} onChange={(v) => set('endOffset', v)} />
            {time('endTime', 'At')}
          </div>
          <label className="check">
            <input type="checkbox" checked={d.adjacent} onChange={(e) => set('adjacent', e.target.checked)} />
            On a Friday it takes in the weekend after; on a Monday, the weekend before
          </label>
          {party(true)}
        </>
      )}

      <div className="preview" aria-live="polite">
        <small>Reads as</small>
        {sentence ?? <span className="muted">Fill in the fields above to see the sentence.</span>}
      </div>

      <div className="two">
        <Field label="Plan clause" hint="Where this comes from, e.g. §II.C">
          <input className="input" required value={clause} onChange={(e) => setClause(e.target.value)} />
        </Field>
        <Field label={current ? 'This version takes effect on' : 'Takes effect on'} hint={current ? `After ${current.effectiveFrom}. Earlier dates keep the old version.` : 'The day the order took effect, usually'}>
          <input className="input" type="date" required value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
        </Field>
      </div>
      {current && (
        <Field label="Why it changed" hint="Shown in the rule’s history and in reports">
          <input className="input" required value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      )}
      <details className="more">
        <summary>Priority</summary>
        <Field label="Priority over other rules" hint="Higher wins where rules overlap. Defaults: regular 10, midweek 20, blocks 30, breaks 40, special days 50, holidays 60.">
          <input className="input" inputMode="numeric" placeholder="Default" value={tier} onChange={(e) => setTier(e.target.value)} />
        </Field>
      </details>
      <ErrorNote error={error} />
      <div className="actions">
        <button className="btn btn-primary" disabled={busy}>
          {current ? 'Save new version' : 'Add rule'}
        </button>
      </div>
    </form>
  );
}

function PartyPicker({ value, onChange, plan, alternate }: { value: PartyRef; onChange: (p: PartyRef) => void; plan: Plan; alternate: boolean }) {
  const alternating = typeof value !== 'string';
  const [a, b] = [plan.parties[0]!.id, plan.parties[1]!.id];
  const select = (v: string, set: (id: string) => void, label: string): ReactNode => (
    <Field label={label}>
      <select className="input" value={v} onChange={(e) => set(e.target.value)}>
        {plan.parties.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </Field>
  );
  return (
    <div className="form">
      {alternate && (
        <label className="check">
          <input type="checkbox" checked={alternating} onChange={(e) => onChange(e.target.checked ? { even: a, odd: b } : a)} />
          Alternates between even and odd years
        </label>
      )}
      {alternating ? (
        <div className="two">
          {select(value.even, (id) => onChange({ ...value, even: id }), 'In even years')}
          {select(value.odd, (id) => onChange({ ...value, odd: id }), 'In odd years')}
        </div>
      ) : (
        select(value, onChange, 'Who has your child')
      )}
    </div>
  );
}

function MonthDay({ month, day, onChange }: { month: string; day: string; onChange: (m: string, d: string) => void }) {
  return (
    <div className="two">
      <Field label="Month">
        <select className="input" value={month} onChange={(e) => onChange(e.target.value, day)}>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Day">
        <input className="input" inputMode="numeric" required value={day} onChange={(e) => onChange(month, e.target.value)} />
      </Field>
    </div>
  );
}

function Edge(props: {
  label: string;
  fixed: boolean;
  onFixed: (v: boolean) => void;
  plain: string;
  month: string;
  day: string;
  time: string;
  onChange: (m: string, d: string, t: string) => void;
}) {
  return (
    <fieldset className="field">
      <legend>{props.label}</legend>
      <div className="seg" role="group">
        <button type="button" aria-pressed={!props.fixed} onClick={() => props.onFixed(false)}>
          {props.plain}
        </button>
        <button type="button" aria-pressed={props.fixed} onClick={() => props.onFixed(true)}>
          On a set date
        </button>
      </div>
      {props.fixed && (
        <div className="three">
          <Field label="Month">
            <select className="input" value={props.month} onChange={(e) => props.onChange(e.target.value, props.day, props.time)}>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Day">
            <input className="input" inputMode="numeric" required value={props.day} onChange={(e) => props.onChange(props.month, e.target.value, props.time)} />
          </Field>
          <Field label="Time">
            <input className="input" type="time" required value={props.time} onChange={(e) => props.onChange(props.month, props.day, e.target.value)} />
          </Field>
        </div>
      )}
    </fieldset>
  );
}

function Offset({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label}>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="-2">2 days before</option>
        <option value="-1">The day before</option>
        <option value="0">On the day</option>
        <option value="1">The day after</option>
        <option value="2">2 days after</option>
      </select>
    </Field>
  );
}
